'use client';

import { cn } from '@kit/ui/utils';

import { useNativePresentation } from '~/(tenant)/_components/native/_lib/use-native-presentation';

import { useEffect, useState } from 'react';

import { QRCodeSVG } from 'qrcode.react';
import { useTranslation } from 'react-i18next';

import {
  computeMemberQrPayload,
  msUntilNextWindow,
} from '@kit/guest-passes';
import { Alert, AlertDescription } from '@kit/ui/alert';
import { Badge } from '@kit/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
} from '@kit/ui/dialog';
import { Trans } from '@kit/ui/trans';

import { useNativeTokens } from '~/(tenant)/_components/native/_lib/use-native-tokens';

import type { MembershipStatus } from '../member-id-card-section';

interface RotatingQrCardProps {
  membershipId: string;
  /**
   * Rotating QR seed. Lives on the device so the gate scanner can verify
   * the live HMAC even when network is flaky. The server delivers it once;
   * see member-id-card-section.tsx for the rationale.
   *
   * Null for non-active memberships — the QR is replaced by a disabled
   * placeholder in that case.
   */
  qrSecret: string | null;
  status: MembershipStatus;
  memberName: string;
  planName: string;
  memberNumber: string;
}

// Mirrors the admin component (member-digital-id-card.tsx). Keep these two
// maps in sync — they're the source of truth for status presentation.
const STATUS_BADGE_VARIANT: Record<
  Exclude<MembershipStatus, 'cancelled'>,
  'success' | 'warning' | 'destructive' | 'secondary'
> = {
  active: 'success',
  pending: 'warning',
  approved: 'warning',
  expired: 'destructive',
  suspended: 'destructive',
  on_hold: 'secondary',
};

const STATUS_LABEL_KEY: Record<
  Exclude<MembershipStatus, 'cancelled'>,
  { i18nKey: string; defaults: string }
> = {
  active: {
    i18nKey: 'common:nativeHome.memberIdCard.status.active',
    defaults: 'Active',
  },
  pending: {
    i18nKey: 'common:nativeHome.memberIdCard.status.pending',
    defaults: 'Pending Approval',
  },
  approved: {
    i18nKey: 'common:nativeHome.memberIdCard.status.approved',
    defaults: 'Awaiting Payment',
  },
  expired: {
    i18nKey: 'common:nativeHome.memberIdCard.status.expired',
    defaults: 'Expired',
  },
  suspended: {
    i18nKey: 'common:nativeHome.memberIdCard.status.suspended',
    defaults: 'Suspended',
  },
  on_hold: {
    i18nKey: 'common:nativeHome.memberIdCard.status.onHold',
    defaults: 'On Hold',
  },
};

/**
 * Member ID card with a TOTP-style rotating QR.
 *
 * Rebuilds the payload every 30 seconds aligned to the wall clock (not the
 * mount time) so two members tapping at the same instant see the same window
 * boundary. Visual countdown ring drains over the window; tap opens a
 * fullscreen dialog with the same live rotation.
 *
 * The HMAC computation is delegated to `@kit/guest-passes/computeMemberQrPayload`
 * — that is the single source of truth shared by the DB verifier and the
 * server-side member-qr generator. Do NOT inline a second implementation here:
 * even tiny encoding differences (raw-bytes vs. decimal-string window index,
 * base64-decoded vs. utf8 secret bytes) silently break every gate scan.
 */
export function RotatingQrCard({
  membershipId,
  qrSecret,
  status,
  memberName,
  planName,
  memberNumber,
}: RotatingQrCardProps) {
  const presentation = useNativePresentation();
  const t = useNativeTokens();
  const [open, setOpen] = useState(false);

  // Per PRD: cancelled memberships should not render a card at all.
  // The parent section also short-circuits, but we guard here as defence in
  // depth so a future caller can't accidentally show a cancelled card.
  const isActive = status === 'active';
  const canRotate = isActive && Boolean(qrSecret);
  const displayPayload = useRotatingQrPayload(membershipId, qrSecret, isActive);

  if (status === 'cancelled') return null;

  const statusBadge = <MemberStatusBadge status={status} />;

  const banner = !isActive ? <StatusBanner status={status} /> : null;

  return (
    <>
      <button
        type="button"
        data-test="member-id-card-trigger"
        onClick={() => setOpen(true)}
        style={{
          display: 'block',
          width: '100%',
          textAlign: 'left',
          padding: 0,
          margin: 0,
          background: 'transparent',
          border: 'none',
          cursor: 'pointer',
          color: t.foreground,
        }}
      >
        <article
          style={{
            padding: 18,
            borderRadius: t.radius,
            border: `1px solid ${t.line}`,
            backgroundColor: t.surface,
            display: 'flex',
            flexDirection: 'column',
            gap: 14,
          }}
        >
          <div style={{ display: 'flex', gap: 16, alignItems: 'center' }}>
            <QrSurface
              payload={displayPayload}
              size={96}
              disabled={!canRotate}
            />

            <div style={{ flex: 1, minWidth: 0 }}>
              {/* Status badge replaces the old plan-label slot. Plan name now
                  sits beneath it so members can still see which package the
                  card is tied to. */}
              <div style={{ marginBottom: 6 }}>{statusBadge}</div>
              {planName ? (
                <div
                  style={{
                    fontFamily: t.fontMono,
                    fontSize: 9,
                    letterSpacing: '0.18em',
                    textTransform: 'uppercase',
                    color: t.muted,
                    marginBottom: 4,
                  }}
                >
                  {planName}
                </div>
              ) : null}
              <div
                style={{
                  fontFamily: t.fontDisplay,
                  fontSize: 20,
                  fontWeight: 400,
                  lineHeight: 1.15,
                  color: t.foreground,
                }}
              >
                {memberName}
              </div>
              {memberNumber ? (
                <div
                  style={{
                    fontFamily: t.fontMono,
                    fontSize: 10,
                    letterSpacing: '0.14em',
                    color: t.muted,
                    marginTop: 6,
                  }}
                >
                  {memberNumber}
                </div>
              ) : null}
              <div
                style={{
                  fontFamily: t.fontMono,
                  fontSize: 8,
                  letterSpacing: '0.18em',
                  textTransform: 'uppercase',
                  color: t.muted,
                  marginTop: 10,
                  opacity: 0.8,
                }}
              >
                <Trans
                  i18nKey="common:nativeHome.memberIdCard.tapToShow"
                  defaults="Tap to show"
                />
              </div>
            </div>
          </div>

          {banner}
        </article>
      </button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent
          className={cn('sm:max-w-md', presentation.className)}
          style={presentation.style}
        >
          <DialogTitle className="sr-only">
            <Trans
              i18nKey="common:nativeHome.memberIdCard.dialogTitle"
              defaults="Member identification"
            />
          </DialogTitle>
          <DialogDescription className="sr-only">
            <Trans
              i18nKey="common:nativeHome.memberIdCard.dialogDescription"
              defaults="Show this code to reception. The code refreshes every 30 seconds."
            />
          </DialogDescription>

          <div className="flex flex-col items-center gap-5 py-4">
            <div>{statusBadge}</div>
            {planName ? (
              <div
                style={{
                  fontFamily: t.fontMono,
                  fontSize: 9,
                  letterSpacing: '0.2em',
                  textTransform: 'uppercase',
                  color: t.muted,
                }}
              >
                {planName}
              </div>
            ) : null}
            <div
              style={{
                fontFamily: t.fontDisplay,
                fontSize: 28,
                fontWeight: 300,
                lineHeight: 1.1,
                color: t.foreground,
                textAlign: 'center',
              }}
            >
              {memberName}
            </div>

            <QrSurface payload={displayPayload} size={260} disabled={!canRotate} />

            {memberNumber ? (
              <div
                style={{
                  fontFamily: t.fontMono,
                  fontSize: 11,
                  letterSpacing: '0.14em',
                  color: t.muted,
                }}
              >
                {memberNumber}
              </div>
            ) : null}
            {isActive ? (
              <div
                style={{
                  fontFamily: t.fontSans,
                  fontSize: 11,
                  color: t.muted,
                  textAlign: 'center',
                  maxWidth: 280,
                }}
              >
                <Trans
                  i18nKey="common:nativeHome.memberIdCard.refreshHint"
                  defaults="Refreshes every 30 seconds for security."
                />
              </div>
            ) : (
              <div className="w-full">{banner}</div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}

/**
 * Live rotating QR payload, rebuilt every 30 seconds aligned to the wall
 * clock (not the mount time) so two members tapping at the same instant see
 * the same window boundary. Returns null while disabled or computing, and
 * never a payload computed for a different membership.
 */
export function useRotatingQrPayload(
  membershipId: string,
  qrSecret: string | null,
  enabled: boolean,
): string | null {
  const [state, setState] = useState<{
    key: string;
    payload: string;
  } | null>(null);
  const canRotate = enabled && Boolean(qrSecret);
  // A rotated secret must never show the old secret's code.
  const stateKey = `${membershipId}:${qrSecret ?? ''}`;

  // Justified useEffect: schedules the rotating-QR refresh aligned to the
  // wall-clock 30s boundary. A timer is the entire purpose of this hook —
  // the alignment is also why we can't replace it with a static derivation.
  // Also recomputes when the app/WebView returns to the foreground, since
  // timers are throttled in the background and the code may be stale.
  // Skipped entirely when disabled so we don't burn cycles computing HMACs.
  // The payload is gated at return time instead of cleared via setState,
  // which avoids the cascading-render lint.
  useEffect(() => {
    if (!canRotate || !qrSecret) return;

    let cancelled = false;
    let timeout: ReturnType<typeof setTimeout> | undefined;
    const key = `${membershipId}:${qrSecret}`;

    const tick = async () => {
      if (cancelled) return;
      if (timeout) clearTimeout(timeout);
      const payload = await computeMemberQrPayload(membershipId, qrSecret);
      if (cancelled) return;
      setState({ key, payload });
      // A foreground tick can overlap a timer tick; keep a single timer.
      if (timeout) clearTimeout(timeout);
      timeout = setTimeout(tick, Math.max(250, msUntilNextWindow()));
    };

    const onVisible = () => {
      if (document.visibilityState === 'visible') void tick();
    };

    void tick();
    document.addEventListener('visibilitychange', onVisible);

    return () => {
      cancelled = true;
      if (timeout) clearTimeout(timeout);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [canRotate, membershipId, qrSecret]);

  return canRotate && state?.key === stateKey ? state.payload : null;
}

export function MemberStatusBadge({
  status,
}: {
  status: Exclude<MembershipStatus, 'cancelled'>;
}) {
  return (
    <Badge
      data-test="member-id-status-badge"
      variant={STATUS_BADGE_VARIANT[status]}
      className="capitalize"
    >
      <Trans
        i18nKey={STATUS_LABEL_KEY[status].i18nKey}
        defaults={STATUS_LABEL_KEY[status].defaults}
      />
    </Badge>
  );
}

// Status-specific banner. Amber/red `warning` for pending/expired/suspended,
// sky/blue `info` for on_hold. Mirrors the PRD copy verbatim so support can
// triage by message.
export function StatusBanner({
  status,
}: {
  status: Exclude<MembershipStatus, 'active' | 'cancelled'>;
}) {
  // 'approved' = plan approved, awaiting the first payment.
  if (status === 'pending' || status === 'approved') {
    return (
      <Alert variant="warning">
        <AlertDescription>
          <Trans
            i18nKey="common:nativeHome.memberIdCard.banner.pending"
            defaults="Awaiting payment to activate your membership"
          />
        </AlertDescription>
      </Alert>
    );
  }

  if (status === 'expired') {
    return (
      <Alert variant="warning">
        <AlertDescription>
          <Trans
            i18nKey="common:nativeHome.memberIdCard.banner.expired"
            defaults="Your membership has expired — please renew"
          />
        </AlertDescription>
      </Alert>
    );
  }

  if (status === 'suspended') {
    return (
      <Alert variant="warning">
        <AlertDescription>
          <Trans
            i18nKey="common:nativeHome.memberIdCard.banner.suspended"
            defaults="Your membership is suspended — please contact the team"
          />
        </AlertDescription>
      </Alert>
    );
  }

  // on_hold
  return (
    <Alert variant="info">
      <AlertDescription>
        <Trans
          i18nKey="common:nativeHome.memberIdCard.banner.onHold"
          defaults="Your membership is on hold"
        />
      </AlertDescription>
    </Alert>
  );
}

// QR surface with a draining countdown ring. Re-renders every 30s when the
// HMAC payload rotates; the ring animation is keyed by payload so it restarts
// in lock-step with the new code.
//
// `disabled` is set when the membership status is not 'active'. In that case
// we render a muted placeholder square (no live HMAC, no ring) so the member
// sees the card structure but understands the QR is intentionally suppressed.
export function QrSurface({
  payload,
  size,
  disabled = false,
}: {
  payload: string | null;
  size: number;
  disabled?: boolean;
}) {
  const t = useNativeTokens();
  const { t: tt } = useTranslation();
  const ringSize = size + 16;
  const radius = (ringSize - 4) / 2;
  const circumference = 2 * Math.PI * radius;

  return (
    <div
      style={{
        position: 'relative',
        width: ringSize,
        height: ringSize,
        flexShrink: 0,
      }}
      data-test="rotating-qr-surface"
      data-disabled={disabled ? 'true' : undefined}
    >
      <svg
        width={ringSize}
        height={ringSize}
        viewBox={`0 0 ${ringSize} ${ringSize}`}
        style={{ position: 'absolute', inset: 0 }}
        aria-hidden
      >
        <circle
          cx={ringSize / 2}
          cy={ringSize / 2}
          r={radius}
          fill="none"
          stroke={t.line}
          strokeWidth={2}
        />
        {!disabled && payload ? (
          <circle
            key={payload}
            cx={ringSize / 2}
            cy={ringSize / 2}
            r={radius}
            fill="none"
            stroke={t.accent}
            strokeWidth={2}
            strokeLinecap="round"
            strokeDasharray={circumference}
            transform={`rotate(-90 ${ringSize / 2} ${ringSize / 2})`}
            style={{
              animation: `member-qr-drain 30s linear forwards`,
            }}
          />
        ) : null}
      </svg>

      <div
        style={{
          position: 'absolute',
          top: 8,
          left: 8,
          width: size,
          height: size,
          borderRadius: t.radius,
          backgroundColor: disabled ? '#f3f4f6' : '#fff',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: 8,
          overflow: 'hidden',
          // Subtle dashed border on disabled state so it reads as "intentionally
          // off" rather than "still loading".
          ...(disabled
            ? { border: `1px dashed ${t.line}` }
            : null),
        }}
        data-test={
          disabled ? 'rotating-qr-surface-disabled' : undefined
        }
      >
        {!disabled && payload ? (
          <QRCodeSVG
            value={payload}
            size={size - 16}
            level="M"
            marginSize={0}
            aria-label={tt('common:memberClub.idCard.qrAriaLabel', { defaultValue: 'Member QR code' })}
            role="img"
          />
        ) : (
          <span
            style={{
              fontFamily: t.fontMono,
              fontSize: 8,
              letterSpacing: '0.18em',
              textTransform: 'uppercase',
              color: '#777',
            }}
            aria-hidden={disabled ? undefined : true}
          >
            {disabled ? '—' : '…'}
          </span>
        )}
      </div>

      <style>{`
        @keyframes member-qr-drain {
          from { stroke-dashoffset: 0; }
          to   { stroke-dashoffset: ${circumference}; }
        }
      `}</style>
    </div>
  );
}
